import { Injectable, signal } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BeneficiaryService, GetBeneficiariesDto } from '../services/beneficiary.service';
import { Beneficiary, Pagination } from './types';
import { BankAccountsStore } from './bank-accounts.store';

@Injectable({
  providedIn: 'root'
})
export class BeneficiariesStore {
  loading = signal(false);
  loadingDescription = signal('');
  beneficiaries = signal<Beneficiary[]>([]);
  pagination = signal<Pagination>(null);
  paginationInput = signal<{ page: number; limit: number }>({ page: 1, limit: 500 });
  currentFilter = signal<GetBeneficiariesDto>({});

  constructor(
    private beneficiaryService: BeneficiaryService,
    private bankAccountsStore: BankAccountsStore,
    private matSnackBar: MatSnackBar
  ) {}

	clearFilters() {
		this.currentFilter.set({})
	}
  async loadBeneficiaries(filter: GetBeneficiariesDto = {}) {
    // Merge with existing filter and account_number
    const mergedFilter = { 
      ...this.currentFilter(), 
      ...filter, 
      account_number: this.bankAccountsStore.selectedBankAccountId() 
    };
    
    // Remove undefined values from mergedFilter
    Object.keys(mergedFilter).forEach(key => {
      if (mergedFilter[key] === undefined) {
        delete mergedFilter[key];
      }
    });
    
    // Store the filter for future use (excluding account_number which is always added)
    const filterToStore = { ...this.currentFilter(), ...filter };
    // Remove undefined values from stored filter
    Object.keys(filterToStore).forEach(key => {
      if (filterToStore[key] === undefined) {
        delete filterToStore[key];
      }
    });
    this.currentFilter.set(filterToStore);

    this.loading.set(true);
    try {
      const response = await this.beneficiaryService.getBeneficiaries(
        this.paginationInput(),
        mergedFilter
      );
      if (response.data?.elements) {
        const { elements, pagination } = response.data;

        // Only append if we're loading more pages (page > 1), otherwise replace
        if (this.paginationInput().page > 1 && this.beneficiaries().length > 0) {
          this.beneficiaries.set([...this.beneficiaries(), ...elements]);
        } else {
          this.beneficiaries.set(elements as Beneficiary[]);
        }
        this.pagination.set(pagination);

        return this.beneficiaries();
      } else if (response.error) {
        this.showLoadError(response.error?.message);
      }
    } catch (error) {
      this.showLoadError();
    } finally {
      this.loading.set(false);
    }
    return null;
  }

  async reloadBeneficiaries() {
    this.paginationInput.set({ page: 1, limit: 500 });
    this.beneficiaries.set([]);
    await this.loadBeneficiaries(this.currentFilter());
  }

  loadMore() {
    if (this.pagination()?.hasNextPage) {
      this.paginationInput.update((current) => ({
        ...current,
        page: current.page + 1
      }));
      this.loadBeneficiaries(this.currentFilter());
    }
  }

  showLoadError(message?: string) {
    this.matSnackBar.open(message || 'Error al cargar beneficiarios', 'Cerrar', {
      duration: 3000,
      panelClass: 'mat-snack-error'
    });
  }

  /**
   * Clears specific filter field and reloads beneficiaries
   */
  clearFilterField(fieldName: keyof GetBeneficiariesDto) {
    const currentFilterValue = this.currentFilter();
    const newFilter = { ...currentFilterValue };
    delete newFilter[fieldName];
    this.currentFilter.set(newFilter);
    this.reloadBeneficiaries();
  }

  /**
   * Clears specific filter field without reloading (used when navigating away or logging out)
   */
  clearFilterFieldOnly(fieldName: keyof GetBeneficiariesDto) {
    const currentFilterValue = this.currentFilter();
    const newFilter = { ...currentFilterValue };
    delete newFilter[fieldName];
    this.currentFilter.set(newFilter);
  }

  /**
   * Clears all debts data from the store
   * Used when a user logs out to ensure no data remains in memory
   */
  clearStore() {
    this.loading.set(false);
    this.loadingDescription.set('');
    this.beneficiaries.set([]);
    this.currentFilter.set({});
  }
}
